import React, { useState, useRef } from "react";
import { beepAndVibrate } from "../utils/feedback";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  ArrowLeft,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import geminiService from "../services/gemini";
import { compressImage } from "../utils/imageCompression";

const DarkSpotScreen = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedImageFile, setCapturedImageFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);

  const handleImageUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      // Store the File object for efficient processing
      setCapturedImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target.result);
        setAnalysisResult(null);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Please select a valid image file");
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const analyzeImage = async () => {
    if (!capturedImage) return;

    setIsAnalyzing(true);
    try {
      let buffer;
      
      // Use File object with standardized compression
      if (capturedImageFile) {
        const compressed = await compressImage(capturedImageFile, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
        });
        const arrayBuffer = await compressed.arrayBuffer();
        buffer = new Uint8Array(arrayBuffer);
      } else {
        // Fallback: convert data URL to buffer
        const response = await fetch(capturedImage);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const compressed = await compressImage(blob, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
        });
        const arrayBuffer = await compressed.arrayBuffer();
        buffer = new Uint8Array(arrayBuffer);
      }

      if (buffer.length === 0) {
        throw new Error("Image buffer is empty. Please try uploading the image again.");
      }

      const result = await geminiService.analyzeDarkSpots(buffer);
      setAnalysisResult(result);
      beepAndVibrate();

      // Show success toast based on classification
      if (result.classification === "no_pills") {
        toast.error("Tidak terdeteksi adanya Pill MMS dalam foto");
      } else if (result.classification === "none") {
        toast.success(
          "MMS quality is good - no significant dark spots detected!"
        );
      } else if (result.classification === "medium") {
        toast("MMS has moderate dark spots - quality is acceptable", {
          icon: "⚠️",
        });
      } else {
        toast.error("MMS has many dark spots - quality is poor");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      
      // Show more detailed error message
      let errorMessage = "Failed to analyze image. Please try again.";
      
      if (error.message) {
        if (error.message.includes("API key")) {
          errorMessage = "Gemini API key is not configured. Please check your environment variables.";
        } else if (error.message.includes("quota")) {
          errorMessage = "API quota exceeded. Please check your Gemini API plan.";
        } else if (error.message.includes("HTTP error")) {
          errorMessage = `API request failed: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const resetAnalysis = () => {
    setCapturedImage(null);
    setCapturedImageFile(null);
    setAnalysisResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case "no_pills":
        return "text-red-600 bg-red-100";
      case "none":
        return "text-green-600 bg-green-100";
      case "medium":
        return "text-yellow-600 bg-yellow-100";
      case "many":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getClassificationIcon = (classification) => {
    switch (classification) {
      case "no_pills":
        return <XCircle className="h-5 w-5" />;
      case "none":
        return <CheckCircle className="h-5 w-5" />;
      case "medium":
        return <AlertCircle className="h-5 w-5" />;
      case "many":
        return <XCircle className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getClassificationText = (classification) => {
    switch (classification) {
      case "no_pills":
        return "Tidak Ada Pill MMS";
      case "none":
        return "Kualitas Baik";
      case "medium":
        return "Kualitas Sedang";
      case "many":
        return "Kualitas Buruk";
      default:
        return "Tidak Diketahui";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Dark Spot Detection
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
            MMS Quality Analysis
          </h2>
          <p className="text-blue-800 dark:text-blue-300 text-sm">
            Upload or capture a photo of MMS pills to analyze for dark spots and
            determine quality. The AI will classify the pills as good, moderate,
            or poor quality based on dark spot detection.
          </p>
        </div>

        {/* Image Upload Section */}
        {!capturedImage && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Upload Image
            </h3>

            <div className="max-w-md mx-auto">
              {/* Upload from Gallery */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                <Upload className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <h4 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                  Upload Image
                </h4>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-6">
                  Select an image of MMS pills from your device
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Choose File
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Captured Image and Analysis */}
        {capturedImage && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Captured Image
              </h3>
              <button
                onClick={resetAnalysis}
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Image Preview */}
              <div>
                <img
                  src={capturedImage}
                  alt="Captured MMS"
                  className="w-full h-auto rounded-lg border border-gray-200 dark:border-gray-600"
                />
              </div>

              {/* Analysis Controls */}
              <div className="flex flex-col justify-center">
                {!analysisResult && (
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Ready to analyze this image for dark spots?
                    </p>
                    <button
                      onClick={analyzeImage}
                      disabled={isAnalyzing}
                      className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isAnalyzing ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Analyzing...
                        </div>
                      ) : (
                        "Analyze Dark Spots"
                      )}
                    </button>
                  </div>
                )}

                {/* Analysis Results */}
                {analysisResult && (
                  <div className="space-y-4">
                    <div className="text-center">
                      <div
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getClassificationColor(
                          analysisResult.classification
                        )}`}
                      >
                        {getClassificationIcon(analysisResult.classification)}
                        <span className="ml-2">
                          {getClassificationText(analysisResult.classification)}
                        </span>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        Analysis Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-300">
                            Confidence:
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {(analysisResult.confidence * 100).toFixed(1)}%
                          </span>
                        </div>
                        {analysisResult.spotCount && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">
                              Spot Count:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {analysisResult.spotCount}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        Description
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {analysisResult.description}
                      </p>
                    </div>

                    <button
                      onClick={resetAnalysis}
                      className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Analyze Another Image
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DarkSpotScreen;
